import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Trophy, Gauge, Wrench, Palette, Sparkles, Star } from "lucide-react";
import { endpoints, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login, hasRole } = useAuth();
  const [car, setCar] = useState(null);
  const [voted, setVoted] = useState(false);
  const [fav, setFav] = useState(false);
  const [score, setScore] = useState({ design: 8, engineering: 8, finish: 8, wow: 8, notes: "" });
  const [savedScore, setSavedScore] = useState(null);

  const load = () => endpoints.car(id).then(setCar).catch(() => {});
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!user) return;
    api.get("/my-votes").then((r) => car && setVoted(r.data[car.category_id] === car.id)).catch(() => {});
    api.get("/favourites").then((r) => setFav(r.data.some((c) => c.id === id))).catch(() => {});
    if (hasRole("judge")) api.get("/my-scores").then((r) => { if (r.data[id]) { setSavedScore(r.data[id]); setScore(r.data[id]); } }).catch(() => {});
  }, [user, car?.id]);

  const vote = async () => {
    if (!user) return login();
    const res = await api.post(`/cars/${id}/vote`);
    setVoted(res.data.voted); toast.success(res.data.message); load();
  };
  const toggleFav = async () => {
    if (!user) return login();
    const res = await api.post(`/cars/${id}/favourite`);
    setFav(res.data.favourited);
  };
  const submitScore = async () => {
    const res = await api.post(`/cars/${id}/score`, score);
    setSavedScore({ ...score, total: res.data.total });
    toast.success(`Score saved: ${res.data.total}/40`);
  };

  if (!car) return <div className="py-20 text-center text-[#8a8d91] font-heading uppercase">Loading...</div>;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#8a8d91] font-heading uppercase text-sm"><ArrowLeft size={16} /> Back</button>
      <div className="relative border border-white/10 overflow-hidden">
        <img src={car.photo_url} alt={`${car.make} ${car.model}`} className="w-full aspect-[4/3] object-cover" data-testid="car-detail-image" />
        {car.is_ai_generated && <span className="absolute top-3 right-3 bg-black/70 text-[#b026ff] text-xs font-heading uppercase px-2 py-1 flex items-center gap-1"><Sparkles size={12} /> AI Render</span>}
      </div>

      <div>
        <div className="text-[#ff7a1a] font-heading uppercase text-sm">{car.year} · {car.category_name}</div>
        <h1 className="font-display text-4xl uppercase text-white leading-none">{car.make} {car.model}</h1>
        <div className="text-[#8a8d91] font-body text-sm mt-1">Exhibited by {car.owner_name}</div>
      </div>

      <div className="flex gap-3">
        <button onClick={vote} data-testid="detail-vote-btn" className={`flex-1 pas-btn text-sm ${voted ? "" : "opacity-90"}`}>
          <Heart size={16} fill={voted ? "currentColor" : "none"} /> {voted ? "Voted" : "Vote"} · {car.vote_count}
        </button>
        <button onClick={toggleFav} data-testid="detail-fav-btn" className="pas-btn-outline text-sm">
          <Star size={16} fill={fav ? "currentColor" : "none"} /> {fav ? "Saved" : "Save"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[{ icon: Gauge, l: "Power", v: car.power_hp ? `${car.power_hp} hp` : "n/a" }, { icon: Palette, l: "Colour", v: car.color || "n/a" }, { icon: Trophy, l: "Votes", v: car.vote_count }].map((s, i) => (
          <div key={i} className="border border-white/10 bg-[#121212] p-3 text-center">
            <s.icon className="mx-auto text-[#ff7a1a] mb-1" size={18} />
            <div className="font-heading text-white text-sm">{s.v}</div>
            <div className="text-[10px] uppercase text-[#8a8d91] font-heading">{s.l}</div>
          </div>
        ))}
      </div>

      {car.description && <p className="text-white/85 font-body leading-relaxed">{car.description}</p>}
      {car.mods && (
        <div className="border border-white/10 bg-[#121212] p-4">
          <div className="flex items-center gap-2 font-heading uppercase text-sm text-white mb-2"><Wrench size={15} className="text-[#ff7a1a]" /> The build</div>
          <p className="text-[#8a8d91] font-body text-sm">{car.mods}</p>
        </div>
      )}

      {hasRole("judge") && (
        <div className="border border-[#b026ff]/40 bg-[#121212] p-4" data-testid="judge-scoring">
          <div className="font-heading uppercase text-[#b026ff] mb-3">Judge Scoring {savedScore && <span className="text-white">· {savedScore.total}/40</span>}</div>
          {["design", "engineering", "finish", "wow"].map((k) => (
            <div key={k} className="mb-3">
              <div className="flex justify-between text-sm mb-1"><span className="font-heading uppercase text-white/85">{k}</span><span className="text-[#ffd600] font-heading">{score[k]}/10</span></div>
              <input type="range" min="0" max="10" value={score[k]} onChange={(e) => setScore({ ...score, [k]: Number(e.target.value) })}
                className="w-full accent-[#ff7a1a]" data-testid={`score-${k}`} />
            </div>
          ))}
          <textarea value={score.notes} onChange={(e) => setScore({ ...score, notes: e.target.value })} placeholder="Notes (optional)" rows={2}
            className="w-full bg-[#0f0f11] border border-white/15 px-3 py-2 text-white text-sm outline-none focus:border-[#b026ff] mt-1" />
          <button onClick={submitScore} className="pas-btn w-full text-sm mt-3" data-testid="submit-score-btn">Save score</button>
        </div>
      )}
    </div>
  );
}
